<template>
  <div>
    <div v-if="prevStepInfo && prevAllowed" :class="$style['back-button']">
      <portal to="mobile-navigation-bottom-bar">
        <v-container>
          <div class="px-6 py-1">
            <BackButton :text="prevStepInfo.title" @click.native="prev" />
          </div>
        </v-container>
      </portal>
    </div>

    <v-stepper v-model="currentStep" :class="$style.stepper" v-bind="$attrs">
      <v-stepper-header :class="$style.header">
        <v-stepper-step
          v-for="(step, index) in steps"
          :key="index"
          :complete="step.titleOnly ? false : currentStep > index + 1"
          :step="index + 1"
          :editable="!nextButtonLoading && (step.titleOnly ? false : currentStep > index + 1)"
          edit-icon
          :class="[
            $style.step,
            {
              [$style['step--first']]: index === 0,
              [$style['step--last']]: index === steps.length - 1,
              [$style['step--visited']]: currentStep > index + 1,
              [$style[`step--n-${currentStep - index - 1}`]]: currentStep > index + 1,
            },
          ]"
          >{{ step.title }}</v-stepper-step
        >
      </v-stepper-header>

      <v-stepper-items :class="$style.items">
        <v-stepper-content v-for="(step, index) in steps" :key="index" :step="index + 1">
          <component
            :is="step.component"
            v-if="!step.titleOnly"
            :first-step="first"
            :next-step="next"
            :prev-step="prev"
            :finish-step="finish"
          />
        </v-stepper-content>
      </v-stepper-items>

      <div v-if="showButtons" :class="$style.buttons">
        <slot v-if="prevAllowed" name="back">
          <BaseButton
            :class="$style.button"
            class="mx-3"
            type="secondary"
            :disabled="currentStep - 1 < 1"
            @click.native="prev"
            >{{ $t('stepper.prev') }}</BaseButton
          >
        </slot>

        <slot v-if="nextAllowed" name="next">
          <BaseButton
            v-if="currentStep !== firstStep"
            :class="$style.button"
            class="mx-3"
            type="primary"
            :disabled="!currentStepisReady || currentStep + 1 > steps.length"
            :loading="nextButtonLoading"
            @click.native="next"
            >{{ $t('stepper.next') }}</BaseButton
          >

          <BaseButton
            v-else
            :class="$style.button"
            class="mx-3"
            type="primary"
            :disabled="!currentStepisReady"
            :loading="nextButtonLoading"
            @click.native="start"
            >{{ $t('stepper.start') }}</BaseButton
          >
        </slot>

        <slot v-if="currentStep === steps.length" name="finish">
          <BaseButton
            :class="$style.button"
            class="mx-3"
            type="primary"
            :disabled="!currentStepisReady"
            :loading="nextButtonLoading"
            @click.native="finish"
            >{{ $t('stepper.done') }}</BaseButton
          >
        </slot>
      </div>

      <PendingDialog :show-dialog="showPendingDialog" />
    </v-stepper>
  </div>
</template>

<script>
import { get, isUndefined } from 'lodash-es'

import { delay } from '@/js/helper/promise-helper.js'

import BackButton from '@/js/components/Shared/BackButton.vue'
import PendingDialog from '@/js/components/Shared/PendingDialog.vue'

export default {
  name: 'StepperElement',
  components: { PendingDialog, BackButton },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    firstStepperStep: {
      type: Number,
      default: undefined,
    },
    currentStepperStep: {
      type: Number,
      default: undefined,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    setStepTo: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      firstStep: this.firstStepperStep || 1,
      currentStep: this.currentStepperStep || this.firstStepperStep || 1,
      nextButtonLoadingValue: false,
      prevButtonLoadingValue: false,
      requestError: undefined,
    }
  },
  computed: {
    currentStepInfo() {
      return get(this.steps, this.currentStep - 1)
    },
    currentStepisReady() {
      return get(this.currentStepInfo, 'isReady', false)
    },
    currentStepContinueAction() {
      return get(this.currentStepInfo, 'continueActionFn')
    },
    prevStepInfo() {
      return get(this.steps, this.currentStep - 2)
    },
    prevAllowed() {
      return this.currentStep !== this.firstStep
    },
    nextAllowed() {
      return this.currentStep < this.steps.length
    },
    continueLoadingDialog() {
      return get(this.currentStepInfo, 'continueLoadingDialog', false)
    },
    showPendingDialog() {
      return this.continueLoadingDialog && (this.nextButtonLoading || this.prevButtonLoading)
    },
    nextButtonLoading: {
      get: function () {
        return this.nextButtonLoadingValue
      },
      set: async function (newValue) {
        if (!newValue && this.continueLoadingDialog) {
          // delay hiding loading dialog
          await delay(150)
          await this.$nextTick()
          this.nextButtonLoadingValue = false
          return
        }
        this.nextButtonLoadingValue = newValue
      },
    },
    prevButtonLoading: {
      get: function () {
        return this.prevButtonLoadingValue
      },
      set: async function (newValue) {
        if (!newValue && this.continueLoadingDialog) {
          // delay hiding loading dialog
          // setTimeout(() => {
          //   this.prevButtonLoadingValue = newValue
          // }, 200)
          await delay(150)
          await this.$nextTick()
          this.prevButtonLoadingValue = false
          return
        }
        this.prevButtonLoadingValue = newValue
      },
    },
  },
  watch: {
    setStepTo: {
      immediate: true,
      handler: function (newValue, oldValue) {
        if (newValue === oldValue) {
          return
        }

        if (isUndefined(newValue)) {
          this.currentStep = this.currentStepperStep || this.firstStepperStep || 1
        }

        this.currentStep = newValue
      },
    },
  },
  methods: {
    start() {
      this.currentStep = this.currentStep + 1
      this.$emit('stepper-element::start')
    },
    first() {
      this.currentStep = this.currentStepperStep || this.firstStepperStep || 1
    },
    async next() {
      if (!this.nextAllowed) {
        return
      }

      if (isUndefined(this.currentStepContinueAction)) {
        this.currentStep = this.currentStep + 1
        this.$emit('stepper-element::next')
        this.$emit('stepper-element::change')
        return
      }
      this.nextButtonLoading = true
      const result = await this.currentStepContinueAction().catch(error => {
        this.requestError = error.message
        this.nextButtonLoading = false
      })
      if (result) {
        this.currentStep = this.currentStep + 1
        this.$emit('stepper-element::next')
        this.$emit('stepper-element::change')
        this.nextButtonLoading = false
        return
      }
      this.nextButtonLoading = false
      this.requestError = this.$t('stepper.error')
    },
    prev() {
      if (!this.prevAllowed) {
        return
      }

      this.currentStep = this.currentStep - 1
      this.$emit('stepper-element::prev')
      this.$emit('stepper-element::change')
    },
    async finish() {
      if (isUndefined(this.currentStepContinueAction)) {
        this.$emit('stepper-element::finish')
        this.$emit('stepper-element::change')
        return
      }
      this.nextButtonLoading = true
      const result = await this.currentStepContinueAction().catch(error => {
        this.requestError = error.message
        this.nextButtonLoading = false
      })
      if (result) {
        this.$emit('stepper-element::finish')
        this.$emit('stepper-element::change')
        this.nextButtonLoading = false
        return
      }
      this.requestError = this.$t('stepper.error')
    },
  },
}
</script>

<style lang="scss" module>
$opacities: 100, 70, 45, 25;

@for $i from 1 through length($opacities) {
  $opacity: nth($opacities, $i) / 100;

  .step--n-#{$i} {
    &::before,
    &::after {
      opacity: #{$opacity};
    }
  }
}

.back-button {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    border: none !important;
  }
}

.stepper,
.header {
  @media screen and (max-width: map-get($grid-breakpoints, 'md') - 1px) {
    border: none !important;
  }

  box-shadow: none !important;
}

.stepper {
  overflow: visible;

  :global(.v-stepper__wrapper) {
    overflow: visible !important;
  }
}

// .stepper {
//   @supports (display: grid) {
//     overflow: hidden;
//   }
// }

.header {
  @media screen and (max-width: map-get($grid-breakpoints, 'md') - 1px) {
    display: none !important;
  }
  position: fixed;
  left: 0;
  z-index: 2;

  width: 100%;

  height: auto !important;

  background: var(--white);

  border: 1px solid var(--grey-lighten-1);
  border-right: none;
  border-left: none;
}

.step {
  position: relative;
  z-index: 1;

  flex: 1;
  justify-content: center;

  padding: 0.95rem 0.5rem;

  cursor: default;

  user-select: none;

  &::before,
  &--first::after,
  &--last::after {
    position: absolute;
    top: 0;

    height: 100%;

    transform: skew(-20deg);

    content: '';
  }

  &::before {
    left: 0;
    z-index: -1;

    width: 100%;

    background: var(--white);
  }

  :global(.v-stepper__step__step) {
    display: none;
  }

  // &:global(.v-stepper__step.v-stepper__step--complete .v-stepper__step__step),
  // &:global(.v-stepper__step.v-stepper__step--error .v-stepper__step__step) {
  //   visibility: visible;
  // }

  :global(.v-stepper__label) {
    display: block; // IE11

    color: var(--black) !important;
    font-weight: var(--font-weight-bold);
    font-size: var(--font-xl);
    line-height: 1;
    text-align: center;
  }

  &--first,
  &--last {
    &::after {
      z-index: -2;

      width: 1rem;
    }
  }

  &--first {
    &::after {
      left: -1rem;
    }
  }

  &--last {
    &::after {
      right: -1rem;
    }
  }

  &--visited {
    &::before,
    &.step--first::after,
    &.step--last::after {
      background: var(--primary);
    }
  }

  &:global(.v-stepper__step--editable) {
    cursor: pointer;
  }

  &:global(.v-stepper__step--active) {
    &::before,
    &.step--first::after,
    &.step--last::after {
      background: var(--black);
    }

    :global(.v-stepper__label) {
      color: var(--primary) !important;
    }
  }
}

.buttons {
  display: flex;
  justify-content: center;

  margin: 3.75rem 0;

  > *:not(:first-child) {
    margin-left: 1.875rem;
  }
}

.items {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    padding-top: 3.75rem;
  }
}
</style>
